export interface Point {
  lat: number;
  lng: number;
}

/** The points of a Google encoded polyline. The planner encodes at precision 6. */
export function decodePolyline(shape: string, precision = 6): Point[] {
  const factor = 10 ** precision;
  const points: Point[] = [];
  let index = 0;
  let lat = 0;
  let lng = 0;
  while (index < shape.length) {
    const deltas: number[] = [];
    for (let part = 0; part < 2; part += 1) {
      let shift = 0;
      let result = 0;
      let byte: number;
      do {
        if (index >= shape.length) return [];
        byte = shape.charCodeAt(index++) - 63;
        result += (byte & 0x1f) * 2 ** shift;
        shift += 5;
      } while (byte >= 0x20);
      deltas.push(result % 2 ? -(result + 1) / 2 : result / 2);
    }
    lat += deltas[0];
    lng += deltas[1];
    points.push({ lat: lat / factor, lng: lng / factor });
  }
  return points;
}

/** The road path of a route when the API sends a shape, else the straight path. */
export function routePath(shape: string | null | undefined, straight: Point[]): Point[] {
  if (!shape) return straight;
  const found = decodePolyline(shape);
  return found.length >= 2 ? found : straight;
}

export const STRAIGHT_TEXT = "Dashed line: no road shape yet";

/** True when the map draws the route as straight lines, because it has no usable road shape. */
export function isStraight(shape: string | null | undefined): boolean {
  return !shape || decodePolyline(shape).length < 2;
}

/** The Polyline options of a route: solid on the roads, a dash of the same colour and weight with no road shape. */
export function lineStyle(straight: boolean, colour: string, opacity: number, weight: number): Record<string, unknown> {
  if (!straight) return { strokeColor: colour, strokeOpacity: opacity, strokeWeight: weight, icons: [] };
  const dash = { path: "M 0,-1 0,1", strokeColor: colour, strokeOpacity: opacity, strokeWeight: weight, scale: 3 };
  return { strokeColor: colour, strokeOpacity: 0, strokeWeight: weight, icons: [{ icon: dash, offset: "0", repeat: "12px" }] };
}

/** The legend text for the trucks drawn as straight lines, or null when every route has a shape. */
export function straightLegend(trucks: string[]): string | null {
  if (!trucks.length) return null;
  const list = trucks.length === 1 ? `truck ${trucks[0]}` : `trucks ${trucks.join(", ")}`;
  return `${STRAIGHT_TEXT} (${list})`;
}
